import React, { useRef } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { PictureAsPdf as PdfIcon, UploadFile as UploadFileIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { formatFileSize } from '../consent-forms/consentFormDisplay';
import { BRAND } from '../../theme/brandTokens';

/** Picks one PDF file. Validation stays with the caller, which passes the error key back. */
const PdfPickerField = ({
  id,
  label,
  file,
  onChange,
  error,
  helpText,
  chooseLabel,
  replaceLabel,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef(null);

  const handleChange = (event) => {
    const nextFile = event.target.files?.[0] ?? null;
    event.target.value = '';
    onChange(nextFile);
  };

  return (
    <Box id={id}>
      {label ? (
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, display: 'block' }}>
          {label}
        </Typography>
      ) : null}
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'stretch', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1.5,
          p: 2,
          border: '2px dashed',
          borderColor: error ? 'error.main' : BRAND.line,
          borderRadius: 2,
          backgroundColor: BRAND.background,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, flex: 1 }}>
          <PdfIcon sx={{ color: file ? '#c62828' : 'text.disabled', flexShrink: 0 }} />
          <Typography variant="body2" color={file ? 'text.primary' : 'text.secondary'} sx={{ wordBreak: 'break-word' }}>
            {file
              ? t('consentForms.management.upload.selected', { name: file.name, size: formatFileSize(file.size) })
              : t('consentForms.management.upload.fileHelp')}
          </Typography>
        </Box>
        <Button
          id={`${id}-choose`}
          variant="outlined"
          startIcon={<UploadFileIcon />}
          onClick={() => inputRef.current?.click()}
          disabled={disabled}
          sx={{ borderColor: BRAND.accent, color: BRAND.accentText, flexShrink: 0, '&:hover': { borderColor: BRAND.accentDark } }}
        >
          {file ? replaceLabel : chooseLabel}
        </Button>
        <input
          ref={inputRef}
          id={`${id}-input`}
          type="file"
          accept="application/pdf,.pdf"
          hidden
          onChange={handleChange}
        />
      </Box>
      {error ? (
        <Typography variant="caption" sx={{ color: '#f44336', mt: 0.5, display: 'block' }}>
          {t(error)}
        </Typography>
      ) : helpText ? (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          {helpText}
        </Typography>
      ) : null}
    </Box>
  );
};

export default PdfPickerField;
